import { useLocation, useNavigate } from "react-router-dom";
import { useCallback } from "react";
import { Tenant } from "@/types/breeze/Tenant";

export const signOutTargetPath = (errorMessage?: string) =>
  errorMessage === "session_expired" || errorMessage === "no_access_to_org"
    ? "session-expired"
    : "logged-out";

// Constants
const PUBLIC_AUTH_PAGES = [
  "/auth/transfer",
  "/auth/confirm-email",
  "/auth/reset-password",
  "/auth/forgot-password",
  "/auth/resend-confirmation",
  "/auth/magic-link",
  "/auth/invites",
  "/auth/logged-out",
  "/auth/session-expired",
  // Test login page itself is the unauthenticated sign-in experience — it must
  // be visitable without an existing session, otherwise visitors testing social
  // sign-in get bounced to /auth/logged-out before they can click anything.
  "/auth/test-login",
] as const;

export const getOrgIdFromPath = (path: string): string | null => {
  const match = path.match(/^\/org\/(\d+)/);
  return match ? match[1] : null;
};

export function useAuthRouting() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Check if pathname is a public auth page, accounting for org prefix in routes
  const isPublicAuthPage = (() => {
    // Strip the optional org-prefix once so the rest of the matchers stay simple.
    const orgPrefixMatch = pathname.match(/^\/org\/\d+/);
    const pathAfterOrg = orgPrefixMatch
      ? pathname.substring(orgPrefixMatch[0].length)
      : pathname;

    if (PUBLIC_AUTH_PAGES.some((page) => pathAfterOrg.startsWith(page))) {
      return true;
    }

    // Social-provider OAuth callback: /auth/{provider}/callback. Driven off a
    // pattern rather than a hardcoded list because we add new providers without
    // wanting to also touch this allow-list each time.
    if (/^\/auth\/[^/]+\/callback($|\?|\/)/.test(pathAfterOrg)) {
      return true;
    }

    return false;
  })();

  const safeNavigate = useCallback(
    (path: string, currentTenant: Tenant | null) => {
      if (path.startsWith("/auth")) {
        navigate(path);
      } else if (currentTenant) {
        const orgPathRegex = new RegExp(`^/org/${currentTenant.id}`);
        if (!orgPathRegex.test(path)) {
          navigate(`/org/${currentTenant.id}${path}`);
        } else {
          navigate(path);
        }
      } else {
        navigate(path);
      }
    },
    [navigate],
  );

  return {
    pathname,
    navigate,
    isPublicAuthPage,
    safeNavigate,
  };
}
